import { useQuery } from "@tanstack/react-query";
import { useRecoilValue } from "recoil";
import ApiController from "../../../controller/ApiController";
import { ConciergeType } from "../../../controller/ConciergeController";
import {
  InterphoneAreaType,
  SENDER_TYPE_ROOM,
} from "../../../controller/InterphoneController";
import {
  AdvertisementType,
  ContentCardType,
  EventType,
  WebStoreSelectorType,
} from "../../../controller/MasterController";
import { areaType } from "../../../recoil/area";
import { authState, masterStoreState } from "../../../recoil/auth";
import { MissedCallType } from "../../../recoil/interphone";
import { langState } from "../../../recoil/lang";
import { TimeSectorRecommendType } from "../../../controller/StoreController";

export const useMainQuery = () => {
  const auth = useRecoilValue(authState);
  const masterStore = useRecoilValue(masterStoreState);
  const lang = useRecoilValue(langState);

  // 메인 데이터
  const { data: storeList } = useQuery<WebStoreSelectorType>({
    queryKey: ["store-list"],
    queryFn: () =>
      ApiController.store
        .getKnockMain(auth.user_id, auth.user_token_id)
        .then((res) => res.data),
    staleTime: 60 * 1000,
    enabled: !!auth.user_id,
  });

  // Area 정보
  const { data: deviceArea } = useQuery<areaType>({
    queryKey: ["device-area"],
    queryFn: () =>
      ApiController.area
        .getAreaByUserToken(masterStore!.store_id)
        .then((res) => res.data.data),
    staleTime: Infinity,
    enabled: !!masterStore && !!masterStore.store_id,
  });

  // 이벤트 목록
  const { data: eventList } = useQuery<EventType[]>({
    queryKey: ["event-list"],
    queryFn: () =>
      ApiController.store
        .getEventList(auth.user_id, auth.user_token_id)
        .then((res) =>
          res.data.data.filter(
            (item: EventType) =>
              (item.event_type === 1 || item.event_type === 3) &&
              item.used === 0
          )
        ),
    gcTime: 30 * 60 * 1000,
    staleTime: 30 * 60 * 1000,
    enabled: !!auth.user_id,
  });

  // 광고 목록
  const { data: adverList } = useQuery<AdvertisementType[]>({
    queryKey: ["adver-list"],
    queryFn: () =>
      ApiController.store
        .getAdverList(auth.user_id, auth.user_token_id)
        .then((res) =>
          res.data.filter((data: AdvertisementType) => data.type === "adver")
        ),
    staleTime: 30 * 60 * 1000,
    enabled: !!auth.user_id,
  });

  // 주변 이벤트
  const { data: aroundCard } = useQuery<ContentCardType[]>({
    queryKey: ["around-card"],
    queryFn: () =>
      ApiController.store.getAroundCard(auth.user_id).then((res) => res.data),
    staleTime: 30 * 60 * 1000,
    enabled: !!auth.user_id,
  });

  // 컨시어지
  const { data: concierge } = useQuery<ConciergeType>({
    queryKey: ["concierge", lang],
    queryFn: () =>
      ApiController.concierge
        .getConciergeList({
          store_id: masterStore!.store_id,
          lang: lang,
        })
        .then((res) => res.data.data),
    enabled: !!masterStore && !!masterStore.store_id,
  });

  // 통화 가능 장소 목록
  const { data: interphoneArea } = useQuery<InterphoneAreaType[]>({
    queryKey: ["interphone-area", lang],
    queryFn: () =>
      ApiController.interphone
        .callAvailableHotelArea({
          hotel_id: masterStore!.store_id,
          lang: lang,
        })
        .then((res) => res.data.data.hotelAreaData || []),
    staleTime: 5 * 60 * 1000,
    enabled: !!masterStore && !!masterStore.store_id,
  });

  // 부재중 목록
  const { data: missedCalls } = useQuery<MissedCallType[]>({
    queryKey: ["missed-calls"],
    queryFn: async () =>
      ApiController.interphone
        .getUnavailableCalls({
          receiver: SENDER_TYPE_ROOM,
          receiver_id: deviceArea!.area_id,
        })
        .then((res) => res.data.data.unavailableCalls || []),
    enabled: !!deviceArea && !!deviceArea.area_id,
  });

  // 인터폰 사용여부
  const { data: useInterphone } = useQuery<number>({
    queryKey: ["use-interphone"],
    queryFn: async () =>
      ApiController.interphone
        .getInterphoneUseStatus({
          hotel_id: masterStore!.store_id,
        })
        .then((res) => res.data.data.isInterphoneUse),
    enabled: !!masterStore && !!masterStore.store_id,
  });

  // 시간대별 매장추천
  const { data: timeSectorRecommend } = useQuery<TimeSectorRecommendType>({
    queryKey: ["timeSector-Recommend", lang],
    queryFn: async () =>
      ApiController.store
        .getTimeSectorRecommend({ lang })
        .then((res) => res.data.data),
  });

  return {
    storeList,
    deviceArea,
    eventList,
    adverList,
    aroundCard,
    concierge,
    interphoneArea,
    missedCalls,
    useInterphone,
    timeSectorRecommend,
  };
};
